import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Sidebar, SidebarContent, SidebarHeader, SidebarMenu, useSidebar } from '../../ui/sidebar';
import { menuItems, eagleGptId } from '../../../constant/sidebar-menu';
import { SidebarMenuItemComponent } from './sidebar-menu-Item';
import { X, Loader2 } from 'lucide-react';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { SidebarHeaderConfig, SidebarHeaderVariant } from '../../../pages/main/main-layout';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { ConversationSearch } from '../conversation-search';
import { deleteConversationById } from '../../../services/storage-service';
import { useToast } from '../../../hooks/use-toast';
import { CustomDialog } from '../custom-dialog/custom-dialog';
import { hasValidConversationComponentRoute, hasValidPlanForStartViaShareLink } from 'pages/services/utilityService';
import { useTranslation } from 'react-i18next';
import { DebouncedInput } from '../debounced-input/debounced-input';
import { useConfig } from 'hooks/use-config';
import { useSubscriptionStore } from 'state/store/subscription';
import { ProjectsSection } from 'features/projects/components/project-section';
import { modalOption } from '/models/sidebar';
import { refetchProject } from 'features/projects/helpers/query-utils';
import { NewProjectModal } from 'features/projects/components/new-project-modal';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateProject } from 'features/projects/hooks/use-create-project';
import { PLANS } from 'constant/plan';
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';

import { useTheme } from 'components/core/theme-provider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { IndividualChatsSection } from 'features/chat/components/individual-chats-section';
import { GroupChatsSection } from 'features/chat/components/group-chats-section';

import groupDark from 'assets/images/bebrave-group-dark.svg';
import groupLight from 'assets/images/bebrave-group.svg';
import { useWhiteLabelStore } from 'state/store/white-label';
import { useGroupShareModal } from 'hooks/use-group-share-modal';
import { GroupShareModal } from 'features/chat/components/GroupShareModal';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { sendInvitation } from 'features/group-chat/services/group-services';

export function LogoSidebarHeader({
  logo,
  smallLogo,
  open,
  isMobile,
  setOpenMobile,
}: SidebarHeaderConfig) {
  return (
    <div className="relative h-10 w-full">
      <img
        src={logo}
        alt="logo"
        className={`absolute left-4 top-1 h-10 w-auto max-w-full transition-all duration-300 ${open || isMobile ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
      />

      <img
        src={smallLogo}
        alt="smallLogo"
        className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-8 w-8 transition-all duration-300 ${open || isMobile ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
      />

      {isMobile && (
        <button
          className="absolute right-4 top-1/2 transform -translate-y-1/2"
          onClick={setOpenMobile ? () => setOpenMobile(false) : undefined}
          aria-label="Close sidebar"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
}

export function IconsSidebarHeader({
  strokeWidth = 2.2,
  toggleSidebar,

  currentPlan,
  // disableNewChat
  open,
}: SidebarHeaderConfig) {
  const { theme } = useTheme();
  const { settings: whiteLabelSettings } = useWhiteLabelStore();

  if (!open) {
    return (
      <div className="h-5 w-full flex items-center justify-center">
        <button
          onClick={toggleSidebar}
          className={`rounded-lg ${theme === 'dark' ? 'hover:bg-primary-200/10' : 'hover:bg-primary-600/10'}`}
        >
          <Icon
            name={'PanelLeftDashed' as IconName}
            size={18}
            strokeWidth={strokeWidth}
            className={theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}
          />
        </button>
      </div>
    );
  }

  // Determine which logo to use based on theme and white-label settings
  const logoSrc = theme === 'dark'
    ? (whiteLabelSettings?.logoUrlDarkMode || groupDark)
    : (whiteLabelSettings?.logoUrl || groupLight);

  return (
    <div className="h-5 w-full flex items-center justify-between">
      <img src={logoSrc} alt="logo" className="h-5 w-[88px]" />

      <div className="h-full flex items-center justify-end gap-2">
        {currentPlan && currentPlan.name != PLANS.NOPLAN ? <ConversationSearch /> : <></>}
        {/* <button
          className="ml-1 p-2 rounded-lg hover:bg-muted-foreground/10 disabled:cursor-not-allowed"
          onClick={onNewChatClick}
          disabled={disableNewChat}
        >
          <Icon name={'SquarePen' as IconName} size={20} strokeWidth={strokeWidth} />
        </button> */}
        <button
          onClick={toggleSidebar}
          className={`rounded-lg ${theme === 'dark' ? 'hover:bg-primary-200/10' : 'hover:bg-primary-600/10'}`}
        >
          <Icon
            name={'PanelLeftDashed' as IconName}
            size={18}
            strokeWidth={strokeWidth}
            className={theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}
          />
        </button>
      </div>
    </div>
  );
}

export function AppSidebar({ type, logo, smallLogo, strokeWidth }: SidebarHeaderConfig) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { pathname } = useLocation();
  const { conversationId: conversationIdFromParams } = useParams<{ conversationId?: string }>();
  const queryClient = useQueryClient();
  const { mutateAsync: updateProject } = useUpdateProject();
  const { setOpenMobile, open, isMobile, openMobile, toggleSidebar } = useSidebar();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationState, setConversationState] = useState<{
    id: string | null;
    type: 'delete' | 'archive' | 'rename' | 'leave' | null;
  }>({
    id: null,
    type: null,
  });
  const integratedMenuItems = menuItems.filter((item) => item.isIntegrated === true);
  // Drag and drop states
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [activeConversation, setActiveConversation] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const [individualPage, setIndividualPage] = useState(0);
  const [groupPage, setGroupPage] = useState(0);
  const pageSize = 50;
  const flatConversationList = useChatStore((state) => state.sidebarConversationsList);
  const totalIndividualConversationsCount = useChatStore((state) => state.totalIndividualConversationsCount);
  const totalGroupConversationsCount = useChatStore((state) => state.totalGroupConversationsCount);

  const individualChats = flatConversationList.filter((c) => !c.IsGroup);
  const groupChats = flatConversationList.filter((c) => c.IsGroup);

  const allIndividualLoaded = individualChats.length >= totalIndividualConversationsCount;
  const allGroupLoaded = groupChats.length >= totalGroupConversationsCount;
  const fetchSidebarConversations = useChatStore((state) => state.fetchSidebarConversations);
  const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
  const fetchAndUpdateProjectAllConversations = useChatStore(
    (state) => state.fetchAndUpdateProjectAllConversations
  );
  const isSidebarLoading = useChatStore((state) => state.isSidebarLoading);
  const { setIsIncognitoChat } = useChatStore();
  const removeConversationFromSidebarById = useChatStore(
    (state) => state.removeConversationFromSidebarById
  );
  const updateConversationTitleInStore = useChatStore(
    (state) => state.updateConversationTitleInStore
  );
  const shareTokens = useChatStore((state) => state.shareTokens);
  const { configurationSettings } = useConfig();

  const { currentPlan } = useSubscriptionStore();

  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isRenameLoading, setIsRenameLoading] = useState(false);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [conversationIdToAddToProject, setConversationIdToAddToProject] = useState<
    string | undefined
  >(undefined);
  const theme = useTheme();
  const [selectedConversationIdForShare, setSelectedConversationIdForShare] = useState<
    string | undefined
  >(undefined);
  // Configure DnD sensors
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  // Use custom hook for group share modal management
  const {
    showModal,
    setShowModal,
    shareToken,
    setShareToken,
    handleOpenModal,
    isLoading: isLoadingModal,
  } = useGroupShareModal(selectedConversationIdForShare);

  useEffect(() => {
    if (currentPlan && currentPlan.name != PLANS.NOPLAN && configurationSettings) {
      fetchSidebarConversations(configurationSettings, { page: individualPage, pageSize, isGroupConversation: false });
    }
  }, [fetchSidebarConversations, configurationSettings, individualPage, pageSize, currentPlan]);

  useEffect(() => {
    if (currentPlan && currentPlan.name != PLANS.NOPLAN && configurationSettings) {
      fetchSidebarConversations(configurationSettings, { page: groupPage, pageSize, isGroupConversation: true });
    }
  }, [fetchSidebarConversations, configurationSettings, groupPage, pageSize, currentPlan]);

  const individualLoaderRef = useRef<HTMLDivElement | null>(null);
  const groupLoaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!individualLoaderRef.current || allIndividualLoaded) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isSidebarLoading) {
          setIndividualPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    observer.observe(individualLoaderRef.current);
    return () => {
      if (individualLoaderRef.current) observer.unobserve(individualLoaderRef.current);
    };
  }, [isSidebarLoading, allIndividualLoaded]);

  useEffect(() => {
    if (!groupLoaderRef.current || allGroupLoaded) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isSidebarLoading) {
          setGroupPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    observer.observe(groupLoaderRef.current);
    return () => {
      if (groupLoaderRef.current) observer.unobserve(groupLoaderRef.current);
    };
  }, [isSidebarLoading, allGroupLoaded]);

  const createConversationMenuOptions = (
    conversationId: string,
    conversationTitle: string,
    shareTokens: Record<string, string>,
    isGroupChat?: boolean
  ): modalOption[] => {
    // Get share token from the centralized store (updated in real-time)
    const shareToken = shareTokens[conversationId];
    const hasShareToken = !!shareToken;

    // Get conversation from sidebar list to check CreatedBy
    const conversation = flatConversationList.find((c) => c.ConversationId === conversationId);
    const currentUser = useCommonStore.getState().user;
    const isCreator = conversation?.CreatedBy === currentUser?.id;

    const baseOptions: modalOption[] = [
      {
        id: 'rename',
        name: t('RENAME'),
        icon: 'Pencil' as IconName,
        callback: () => {
          console.log('Rename Conversation:', conversationId);
          setIsRenameDialogOpen(true);
          setNewTitle(conversationTitle || '');
          setConversationState({
            id: conversationId,
            type: 'rename',
          });
        },
      }
    ];

    if (hasValidPlanForStartViaShareLink(currentPlan)) {
      baseOptions.push({
        id: 'start-group-chat',
        name: hasShareToken
          ? (t('ADD_PARTICIPANT_VIA_LINK') || 'Add Participant via link')
          : (t('START_GROUP_CHAT') || 'Start group chat'),
        icon: 'UserRoundPlus' as IconName,
        isDisabled: isLoadingModal,
        callback: () => {
          console.log('Start Group Chat:', conversationId);
          setSelectedConversationIdForShare(conversationId);
          handleOpenModal();
        },
      });
    }

    if (!isGroupChat) {
      baseOptions.push({
        id: 'add-to-project',
        type: 'submenu' as const,
        name: t('ADD_TO_PROJECT'),
        icon: 'FolderClosed' as IconName,
        subOptions: [
          {
            id: 'new-project',
            name: t('NEW_PROJECT'),
            icon: 'FolderPlus' as IconName,
            callback: () => {
              setIsAddProjectModalOpen(true);
              setConversationIdToAddToProject(conversationId);
            },
          },
          ...sidebarProjectsList.map((project) => ({
            id: project.id,
            name: project.name,
            icon: 'FolderClosed' as IconName,
            callback: () => handleAddToProject(project.id, conversationId),
          })),
        ],
      });
      baseOptions.push({
        id: 'archive',
        name: t('ARCHIVE'),
        icon: 'Archive' as IconName,
        callback: () => {
          console.log('Archive Conversation:', conversationId);
          setIsConfirmDialogOpen(true);
          setConversationState({
            id: conversationId,
            type: 'archive',
          });
        },
      });
    }

    if (isCreator) {
      baseOptions.push({
        id: 'delete',
        name: t('DELETE'),
        icon: 'Trash2' as IconName,
        callback: () => {
          console.log('Delete Conversation:', conversationId);
          setIsConfirmDialogOpen(true);
          setConversationState({
            id: conversationId,
            type: 'delete',
          });
        },
      });
    } else {
      baseOptions.push({
        id: 'leave',
        name: t('LEAVE') || 'Leave',
        icon: 'LogOut' as IconName,
        callback: () => {
          console.log('Leave Conversation:', conversationId);
          setIsConfirmDialogOpen(true);
          setConversationState({
            id: conversationId,
            type: 'leave',
          });
        },
      });
    }

    return baseOptions;
  };

  const handleAddToProject = async (projectId: string, conversationId: string) => {
    try {
      const toProject = sidebarProjectsList.find((p) => p.id === projectId);
      if (!toProject) return;
      const payload = {
        projectId: projectId,
        conversationId: conversationId,
      };
      await updateProject({ payload: payload, skipInvalidate: true });
      await fetchAndUpdateProjectAllConversations(configurationSettings, projectId);
      removeConversationFromSidebarById(conversationId);
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('MOVE_SUCCESS_DESCRIPTION'),
      });
    } catch {
      toast({
        variant: 'destructive',
        title: t('MOVE_FAILED'),
        description: t('MOVE_FAILED_DESCRIPTION'),
      });
    } finally {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
    }
  };

  const handleRename = async (conversationId: string) => {
    try {
      setIsRenameLoading(true);
      await updateProject({ payload: { conversationId, conversationTitle: newTitle } });
      updateConversationTitleInStore(conversationId, newTitle);
      setIsRenameDialogOpen(false);
      setIsRenameLoading(false);
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('RENAME_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsRenameDialogOpen(false);
      setIsRenameLoading(false);
      toast({
        variant: 'destructive',
        title: t('RENAME_FAILED'),
        description: t('RENAME_FAILED_DESCRIPTION'),
      });
      console.error('Error renaming conversation:', error);
    }
  };

  const handleArchive = async (conversationId: string) => {
    try {
      setIsLoading(true);
      await updateProject({ payload: { isArchived: true, conversationId } });
      removeConversationFromSidebarById(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      if (conversationIdFromParams && conversationIdFromParams === conversationId)
        navigate('/chat');
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('ARCHIVE_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('ARCHIVE_FAILED'),
        description: t('ARCHIVE_FAILED_DESCRIPTION'),
      });
      console.error('Error archiving conversation:', error);
    }
  };

  const handleDelete = async (conversationId: string) => {
    try {
      setIsLoading(true);
      await deleteConversationById(conversationId, configurationSettings);
      removeConversationFromSidebarById(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      if (conversationIdFromParams && conversationIdFromParams === conversationId)
        navigate('/chat');
      // toast({
      //   variant: 'success',
      //   title: t('SUCCESS'),
      //   description: t('DELETE_SUCCESS_DESCRIPTION'),
      // });
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('DELETE_FAILED'),
        description: t('DELETE_FAILED_DESCRIPTION'),
      });
      console.error('Error deleting conversation:', error);
    }
  };

  const handleLeave = async (conversationId: string) => {
    try {
      setIsLoading(true);
      const currentUser = useCommonStore.getState().user;
      if (!currentUser?.id || !configurationSettings) {
        throw new Error('User or configuration not available');
      }

      // Get conversation title for the payload
      const conversation = flatConversationList.find((c) => c.ConversationId === conversationId);
      const conversationTitle = conversation?.ConversationTitle || '';

      const payload = {
        invitationType: 'REMOVE_USER' as const,
        userIds: [currentUser.id],
        conversationId: conversationId,
        conversationTitle: conversationTitle,
      } as any; // Type assertion needed as InvitationPayload interface doesn't include userIds and conversationTitle

      await sendInvitation(payload, configurationSettings);
      removeConversationFromSidebarById(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);

      if (conversationIdFromParams && conversationIdFromParams === conversationId) {
        navigate('/chat');
      }

      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('LEAVE_SUCCESS_DESCRIPTION') || 'You have left the conversation',
      });
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('LEAVE_FAILED') || 'Failed to leave',
        description: t('LEAVE_FAILED_DESCRIPTION') || 'Failed to leave the conversation',
      });
      console.error('Error leaving conversation:', error);
    }
  };

  const handleConfirmAction = () => {
    if (conversationState.id && conversationState.type === 'archive') {
      handleArchive(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'delete') {
      handleDelete(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'rename') {
      handleRename(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'leave') {
      handleLeave(conversationState.id);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: any) => {
    const conversationId = event.active.id;
    const conversation = flatConversationList.find((c) => c.ConversationId === conversationId);

    setIsDragging(true);
    setActiveConversation({
      id: conversationId,
      title: conversation?.ConversationTitle || t('NEW_CHAT'),
    });
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setIsDragging(false);
    if (over && over.id) {
      const conversationId = active.id;
      const projectId = over.id;

      const project = sidebarProjectsList.find((p) => p.id === projectId);

      toast({
        title: t('MOVING_CONVERSATION'),
        description: `Moving "${activeConversation?.title}" to "${project?.name}"`,
        duration: 2000,
      });

      handleAddToProject(projectId, conversationId);
    }

    setTimeout(() => {
      setActiveConversation(null);
    }, 0);
  };

  const handleDragCancel = () => {
    setIsDragging(false);
    setActiveConversation(null);
    toast({
      title: 'Drag cancelled',
      description: 'Conversation was not moved',
      variant: 'destructive',
      duration: 2000,
    });

    setTimeout(() => {
      setIsDragging(false);
      setActiveConversation(null);
    }, 150);
  };

  const handleDragOver = (event: any) => {
    const { over } = event;
    if (over) {
      const project = sidebarProjectsList.find((p) => p.id === over.id);
      console.log(`Dragging over project: ${project?.name}`);
    }
  };

  const setNewChat = useChatStore((state) => state.setNewChat);
  const disableNewChat = useChatStore((state) => state.disableNewChat);

  const onNewChatClick = () => {
    if (disableNewChat) return;
    if (isMobile) {
      setOpenMobile(false);
    }
    setNewChat(true);
    setIsIncognitoChat(false);
    navigate('/chat');
  };

  const onIncognitoClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
    setNewChat(true);
    setIsIncognitoChat(true);
    navigate('/chat?incognito-chat=true');
  };

  useEffect(() => {
    if (!isMobile) {
      setOpenMobile(false);
    }
  }, [pathname, setOpenMobile, isMobile]);

  const sidebarStyle = isMobile
    ? ({
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100%',
      zIndex: 50,
      borderRight: 'none',
      transition: 'transform 0.3s ease-in-out',
      transform: openMobile ? 'translateX(0)' : 'translateX(-100%)',
    } as React.CSSProperties)
    : ({
      width: open ? 'var(--sidebar-width)' : '64px',
      minWidth: open ? 'var(--sidebar-width)' : '64px',
      transition: 'width 0.3s ease, min-width 0.3s ease',
      height: '100%',
      borderRight: '0px solid var(--border-color, #e2e8f0)',
    } as React.CSSProperties);



  return (
    <Sidebar
      className={`bg-card h-full ${isMobile ? 'mobile-sidebar' : ''}`}
      collapsible={isMobile ? 'none' : 'icon'}
      style={sidebarStyle}
    >
      <SidebarHeader className="p-4">
        {type === SidebarHeaderVariant.Logo && (
          <LogoSidebarHeader
            type={type}
            logo={logo}
            smallLogo={smallLogo}
            open={open}
            isMobile={isMobile}
            setOpenMobile={setOpenMobile}
          />
        )}

        {type === SidebarHeaderVariant.Icons && (
          <IconsSidebarHeader
            type={type}
            strokeWidth={strokeWidth}
            toggleSidebar={toggleSidebar}
            onIncognitoClick={onIncognitoClick}
            onNewChatClick={onNewChatClick}
            currentPlan={currentPlan}
            disableNewChat={disableNewChat}
            open={open}
          />
        )}
      </SidebarHeader>

      <SidebarContent className="text-base mr-0 text-high-emphasis font-normal group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:pr-0 !overflow-visible flex flex-col">
        <div className="flex-shrink-0 bg-sidebar">
          {integratedMenuItems
            .filter((item) => !((item.id === 'my-files') && (!currentPlan || currentPlan.name === PLANS.NOPLAN)))
            .map((item) => {
              // Add callback to rightIcon if it's the eagleGpt item
              const itemWithCallback = item.id === eagleGptId && item.rightIcon
                ? {
                  ...item,
                  rightIcon: {
                    ...item.rightIcon,
                    callback: () => {
                      if (disableNewChat) return;
                      setNewChat(true);
                      navigate('/chat');
                      if (isMobile) setOpenMobile(false);
                    }
                  }
                }
                : item;

              return (
                <SidebarMenu key={item.id} className="w-full font-medium disabled:cursor-not-allowed group-data-[collapsible=icon]:items-center">
                  <SidebarMenuItemComponent
                    item={itemWithCallback}
                    showText={open || isMobile}
                    isActive={pathname.includes(item.path)}
                    onClick={() => {
                      setNewChat(false);
                      if (isMobile) setOpenMobile(false);
                    }}
                    disabled={item.id === eagleGptId && disableNewChat}
                  />
                </SidebarMenu>
              );
            })}
        </div>

        <div className="flex-1 overflow-auto mt-8">
          {hasValidConversationComponentRoute(pathname) && (
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              onDragOver={handleDragOver}
              modifiers={[restrictToVerticalAxis]}
            >
              {currentPlan && currentPlan.name != PLANS.NOPLAN && <ProjectsSection />}

              {currentPlan && currentPlan.name != PLANS.NOPLAN && (
                <GroupChatsSection
                  conversations={groupChats}
                  isSidebarLoading={isSidebarLoading}
                  allLoaded={allGroupLoaded}
                  loaderRef={groupLoaderRef}
                  renderRightAction={(id, title) => (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="ml-1 p-1 rounded-md hover:bg-muted-foreground/10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Icon
                            name="Ellipsis"
                            size={18}
                            strokeWidth={2.2}
                            className="text-sidebar-foreground group-hover:text-primary"
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side="right" align="end" className="min-w-48 rounded-[8px]">
                        {createConversationMenuOptions(id, title, shareTokens, true).map((option) => {
                          if (option.type === 'submenu') {
                            return (
                              <DropdownMenuSub key={option.id}>
                                <DropdownMenuSubTrigger
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex items-center gap-2"
                                  disabled={option.isDisabled}
                                >
                                  <Icon
                                    name={option.icon as IconName}
                                    size={16}
                                    strokeWidth={2.2}
                                    className="text-primary"
                                  />
                                  <span className="text-sm font-light">{option.name}</span>
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="min-w-48 rounded-[8px]">
                                  {option.subOptions?.map((subOption: any) => (
                                    <DropdownMenuItem
                                      key={subOption.id}
                                      className="flex items-center gap-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!subOption?.isDisabled && subOption?.callback) {
                                          subOption.callback();
                                        }
                                      }}
                                      disabled={subOption?.isDisabled}
                                    >
                                      <Icon
                                        name={subOption.icon as IconName}
                                        size={16}
                                        strokeWidth={2.2}
                                        className="mr-2 text-primary"
                                      />
                                      <span className="text-sm font-light">{subOption.name}</span>
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            );
                          }

                          return (
                            <DropdownMenuItem
                              key={option.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                option.callback?.();
                              }}
                              className={`flex items-center gap-2 ${option.id === 'delete' || option.id === 'leave' ? 'text-destructive hover:text-destructive focus:text-destructive' : ''}`}
                            >
                              <Icon
                                name={option.icon as IconName}
                                size={16}
                                strokeWidth={2.2}
                                className={option.id === 'delete' || option.id === 'leave' ? 'text-destructive' : 'text-primary'}
                              />
                              <span className={`text-sm font-light ${option.id === 'delete' || option.id === 'leave' ? 'text-destructive hover:text-destructive' : ''}`}>{option.name}</span>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                />
              )}

              {currentPlan && currentPlan.name != PLANS.NOPLAN && (
                <IndividualChatsSection
                  conversations={individualChats}
                  isSidebarLoading={isSidebarLoading}
                  allLoaded={allIndividualLoaded}
                  loaderRef={individualLoaderRef}
                  renderRightAction={(id, title) => (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="ml-1 p-1 rounded-md hover:bg-muted-foreground/10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Icon
                            name="Ellipsis"
                            size={18}
                            strokeWidth={2.2}
                            className="text-sidebar-foreground group-hover:text-primary"
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side="right" align="end" className="w-48 rounded-[8px]">
                        {createConversationMenuOptions(id, title, shareTokens, false).map((option) => {
                          if (option.type === 'submenu') {
                            return (
                              <DropdownMenuSub key={option.id}>
                                <DropdownMenuSubTrigger
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex items-center gap-2"
                                  disabled={option.isDisabled}
                                >
                                  <Icon
                                    name={option.icon as IconName}
                                    size={16}
                                    strokeWidth={2.2}
                                    className="text-primary"
                                  />
                                  <span className="text-sm font-light">{option.name}</span>
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="min-w-48 rounded-[8px]">
                                  {option.subOptions?.map((subOption: any) => (
                                    <DropdownMenuItem
                                      key={subOption.id}
                                      className="flex items-center gap-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!subOption?.isDisabled && subOption?.callback) {
                                          subOption.callback();
                                        }
                                      }}
                                      disabled={subOption?.isDisabled}
                                    >
                                      <Icon
                                        name={subOption.icon as IconName}
                                        size={16}
                                        strokeWidth={2.2}
                                        className="mr-2 text-primary"
                                      />
                                      <span className="text-sm font-light">{subOption.name}</span>
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            );
                          }

                          return (
                            <DropdownMenuItem
                              key={option.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                option.callback?.();
                              }}
                              className={`flex items-center gap-2 ${option.id === 'delete' || option.id === 'leave' ? 'text-destructive hover:text-destructive focus:text-destructive' : ''}`}
                            >
                              <Icon
                                name={option.icon as IconName}
                                size={16}
                                strokeWidth={2.2}
                                className={option.id === 'delete' || option.id === 'leave' ? 'text-destructive' : 'text-primary'}
                              />
                              <span className={`text-sm font-light ${option.id === 'delete' || option.id === 'leave' ? 'text-destructive hover:text-destructive' : ''}`}>{option.name}</span>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                />
              )}


              {isSidebarLoading && (
                <div className="flex items-start justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}

              <CustomDialog
                isOpen={isConfirmDialogOpen}
                isLoading={isLoading}
                setIsOpen={setIsConfirmDialogOpen}
                title={
                  conversationState.type === 'delete'
                    ? t('DELETE')
                    : conversationState.type === 'leave'
                      ? t('LEAVE') || 'Leave'
                      : t('CONFIRM_ACTION')
                }
                onConfirm={handleConfirmAction}
                confirmText={t('YES')}
                cancelText={t('CANCEL')}
                description={
                  conversationState.type === 'delete' || conversationState.type === 'leave'
                    ? ''
                    : t('CONFIRM_ACTION_DESCRIPTION')
                }
              >
                <p>
                  {conversationState.type === 'delete'
                    ? t('DELETE_CONFIRMATION_DESCRIPTION') || t('CONFIRM_ACTION_DESCRIPTION')
                    : conversationState.type === 'leave'
                      ? t('LEAVE_CONFIRMATION_DESCRIPTION') || 'Are you sure you want to leave this conversation?'
                      : t('CONFIRM_ACTION_DESCRIPTION')}
                </p>
              </CustomDialog>

              <CustomDialog
                isOpen={isRenameDialogOpen}
                isLoading={isRenameLoading}
                setIsOpen={setIsRenameDialogOpen}
                title={t('RENAME')}
                onConfirm={handleConfirmAction}
                confirmText={t('SAVE')}
                cancelText={t('CANCEL')}
                description={''}
                isConfirmDisabled={newTitle.trim() === ''}
              >
                <DebouncedInput
                  placeholder={t('ENTER_NEW_TITLE')}
                  initialValue={newTitle}
                  onDebouncedChange={setNewTitle}
                  className="
                            h-10 w-full rounded-3xl
                            border border-primary
                            bg-background pr-8
                            focus:outline-none
                            focus:border-primary
                            focus:ring-1 focus:ring-primary
                 "
                />
              </CustomDialog>

              <NewProjectModal
                callback={() => {
                  refetchProject(queryClient, '');
                  removeConversationFromSidebarById(conversationIdToAddToProject!);
                }}
                newConversatioId={conversationIdToAddToProject}
                isOpen={isAddProjectModalOpen}
                onClose={() => setIsAddProjectModalOpen(false)}
              />

              {/* DragOverlay for visual feedback */}
              <DragOverlay modifiers={[restrictToVerticalAxis]}>
                {isDragging && activeConversation ? (
                  <div className="bg-primary-25 rounded-lg px-3 py-2 shadow-lg border border-primary z-[9999]">
                    <p className={`text-sm truncate max-w-[200px] ${theme.theme === 'dark' ? 'text-white' : 'text-foreground'}`}>
                      📄 {activeConversation.title}
                    </p>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      </SidebarContent>

      {/* Group Share Modal */}
      <GroupShareModal
        showModal={showModal}
        setShowModal={setShowModal}
        shareToken={shareToken}
        setShareToken={setShareToken}
        isLoadingConversation={isLoadingModal}
      />
    </Sidebar>
  );
}
